import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { gradeFor, circleSrc, decorationSrc } from '../lib/grade';
import { episodeWord, QUIZ_COPY } from '../lib/quizCopy';
import { episodeTitle } from '../data/episodes';
import type { QuizResult } from '../state/flow';
import './ReportCard.scss';

export type ReportCardVariant = 'full' | 'preview';

/**
 * The Briar U "report card" — rendered over report-bg.jpg at a fixed
 * 1080×1350 design size and scaled to fit its container (see `.report-card-frame`).
 *
 * `variant="full"`: real text. Used on the results screen and as the
 * rasterized shareable PNG (ref forwarded to the root so it can be captured).
 * `variant="preview"`: prose swapped for skeleton bars, but the real letter
 * grade, %, circle, and red marking are kept. Used in the picker gallery.
 */
const ReportCard = forwardRef<HTMLDivElement, { result: QuizResult; variant?: ReportCardVariant }>(
  ({ result, variant = 'full' }, ref) => {
    const { episode, correct, total, firstName, lastName } = result;
    const grade = gradeFor(correct, total);
    const isPreview = variant === 'preview';
    // A/A+ (star) and B (check) float their mark up by the grade; C-and-below
    // (the wide underline) sits under the commentary paragraph.
    const markByGrade = grade.decoration === 'star' || grade.decoration === 'check';
    const takenName = `${firstName} ${lastName}`.trim();

    // Width-only fit for the cursive name: shrink only when it's wider than the
    // form line. (The generic useFitText also checks scrollHeight, which trips
    // inconsistently on Caveat at line-height:1 — Chrome shrank to minScale
    // while Safari stayed at 1. scrollWidth is unaffected by the `scale`
    // property, so this measures the true natural width on every browser.)
    const nameRef = useRef<HTMLSpanElement | null>(null);
    const [nameScale, setNameScale] = useState(1);
    useLayoutEffect(() => {
      const el = nameRef.current;
      const line = el?.parentElement;
      if (!el || !line) return;
      const fit = () => {
        const natural = el.scrollWidth;
        const avail = line.clientWidth - 16; // left inset + a little right air
        setNameScale(natural > 0 ? Math.min(1, avail / natural) : 1);
      };
      fit();
      let alive = true;
      void document.fonts?.ready.then(() => alive && fit()).catch(() => undefined);
      return () => {
        alive = false;
      };
    }, [takenName]);

    // Smart red-underline placement: if the commentary's last line is short
    // (< 40% of the paragraph width), the underline jumps up to the right end
    // of the penultimate line — opposite the short last line — instead of
    // trailing off under it. Measures the wrapped line boxes via a Range.
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const textRef = useRef<HTMLParagraphElement | null>(null);
    const [deco, setDeco] = useState<{ mode: 'flow' | 'penult'; top: number }>({
      mode: 'flow',
      top: 0,
    });

    useLayoutEffect(() => {
      if (isPreview) return;
      const body = bodyRef.current;
      const p = textRef.current;
      if (!body || !p) return;
      const contentW = REPORT_CARD_W - 280; // body width (140px margins each side)
      const measure = () => {
        const range = document.createRange();
        range.selectNodeContents(p);
        const rects = Array.from(range.getClientRects()).filter((r) => r.width > 1);
        const bodyRect = body.getBoundingClientRect();
        const scale = bodyRect.width / contentW; // card may be displayed scaled
        if (rects.length < 2 || scale <= 0) {
          setDeco({ mode: 'flow', top: 0 });
          return;
        }
        const last = rects[rects.length - 1];
        const penult = rects[rects.length - 2];
        if (last.width / scale < contentW * 0.4) {
          setDeco({ mode: 'penult', top: (penult.bottom - bodyRect.top) / scale });
        } else {
          setDeco({ mode: 'flow', top: 0 });
        }
      };
      measure();
      // Wrapping depends on the (async) web fonts — re-measure once they load.
      let alive = true;
      void document.fonts?.ready.then(() => alive && measure()).catch(() => undefined);
      return () => {
        alive = false;
      };
    }, [isPreview, grade.commentary]);

    return (
      <div ref={ref} className={`report-card report-card--${variant}`}>
        <img className="report-card__bg" src="/img/quiz/report-bg.jpg" alt="" />

        {/* Header: Episode N: Title + "Taken By:" form line. Episode title and
            name are real in both variants (only the commentary is skeletoned). */}
        <div className="report-card__header">
          <div className="report-card__episode">
            Episode {episodeWord(episode)}: {episodeTitle(episode)}
          </div>
          <div className="report-card__taken">
            <span className="report-card__taken-label">{QUIZ_COPY.takenBy}</span>
            <span className="report-card__name-line">
              <span
                ref={nameRef}
                className="report-card__name"
                // Dip the baseline onto the rule, scaled with the name so a
                // shrunk long name doesn't drop its baseline below the line
                // (≈0.2em descent of the 80px font = 16px at full size).
                style={{ scale: String(nameScale), bottom: `${(-16 * nameScale).toFixed(1)}px` }}
              >
                {takenName}
              </span>
            </span>
          </div>
        </div>

        {/* Grade headline */}
        <div className="report-card__grade-pct">
          {firstName}'s Grade: {grade.pct}%
        </div>

        {/* Giant letter grade with the episode's red circle */}
        <div className="report-card__grade-wrap">
          <img className="report-card__circle" src={circleSrc(episode)} alt="" />
          <span className="report-card__letter">{grade.letter}</span>
          {markByGrade && (
            <img
              className={`report-card__grade-mark report-card__grade-mark--${grade.decoration}`}
              src={decorationSrc(grade.decoration, episode)}
              alt=""
            />
          )}
        </div>

        {/* Answered X out of N — real in both variants */}
        <div className="report-card__answered">
          Answered{' '}
          <strong>
            {correct} out of {total}
          </strong>{' '}
          questions correctly
        </div>

        {/* Commentary heading — the only text above the baked rule */}
        {isPreview ? (
          <div className="report-card__commentary-head report-card__skeleton report-card__skeleton--heading" />
        ) : (
          <div className="report-card__commentary-head">
            {QUIZ_COPY.professorCommentary}
            {grade.tag && <span className="report-card__tag"> {grade.tag}</span>}
          </div>
        )}

        {/* Commentary body — below the baked rule */}
        <div ref={bodyRef} className="report-card__commentary-body">
          {isPreview ? (
            <>
              <div className="report-card__skeleton report-card__skeleton--line" />
              <div className="report-card__skeleton report-card__skeleton--line" />
              <div className="report-card__skeleton report-card__skeleton--line" />
              <div className="report-card__skeleton report-card__skeleton--line report-card__skeleton--line-short" />
            </>
          ) : (
            <p ref={textRef} className="report-card__commentary-text">
              {grade.commentary}
            </p>
          )}
          {grade.decoration === 'line' && (
            <img
              className={`report-card__decoration report-card__decoration--line${
                deco.mode === 'penult' ? ' report-card__decoration--penult' : ''
              }`}
              style={deco.mode === 'penult' ? { top: `${deco.top}px` } : undefined}
              src={decorationSrc('line', episode)}
              alt=""
            />
          )}
        </div>
      </div>
    );
  },
);

ReportCard.displayName = 'ReportCard';

export default ReportCard;

// Natural design size of the card; the scaler maps the container width to it.
export const REPORT_CARD_W = 1080;
export const REPORT_CARD_H = 1350;

/** Fluidly scales a `<ReportCard>` to fill its container width. CSS can't
 * derive a unitless scale from container width, so a ResizeObserver computes
 * it. Used for on-screen display (results + picker previews); the rasterizer
 * captures the raw, unscaled card instead. */
export function ScaledReportCard({
  result,
  variant = 'full',
}: {
  result: QuizResult;
  variant?: ReportCardVariant;
}) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const measure = () => setScale(el.clientWidth / REPORT_CARD_W);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={frameRef}
      className="report-card-frame"
      style={{ height: scale ? `${scale * REPORT_CARD_H}px` : undefined }}
    >
      <div className="report-card-scale" style={{ transform: `scale(${scale})` }}>
        <ReportCard result={result} variant={variant} />
      </div>
    </div>
  );
}
